import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import {
  IField,
  IFormPage,
  ISelect,
  ISelectOption,
  SelectType,
} from 'app/services/api5-service/api.interface';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { Option, SelectBuilderParams } from '@ispui/select/dist/types';

import { ISP_COLORS, ISP_COLORS_VAR } from 'common/colors';
import { IFormModel } from 'common/dynamic-form/dynamic-form.interface';
import { SelectControlWithValue } from 'common/dynamic-form/utils/form-preparing';
import { getLabel } from 'common/dynamic-form/wrappers/field-base/label/label.utils';
import { FieldSettings } from 'common/dynamic-form/wrappers/field-base/model';
import { matchMediaBreakpointDown$ } from 'common/media';
import { DocHelper } from 'utils/dochelper';

import {
  getDropdownDirection,
  getDropdownParentSelector,
} from './dropdown.utils';
import { SelectOptionsState, SelectProps } from './model';

import {
  ISPFieldConfig,
  ISPFieldType,
  ISPFieldWrapper,
  ISPFormState,
} from '../../model';

/**
 * Whether to hide the select field with an empty list of options.
 * Fields with some select types need to be hidden if they have an empty list of options
 *
 * @param control - control (subfield) metadata
 * @param options - original select options
 */
function isHideSelectWithEmptyOptionsList(
  control: ISelect,
  options: ISelectOption[] | null,
): boolean {
  if (!control.$type) {
    return false;
  }

  const selectTypesNotShowingWithEmptyOptionList = [
    SelectType.SwitcherSelect,
    SelectType.Radio,
    SelectType.RadioImgRender,
    SelectType.Switcher,
  ];

  return (
    selectTypesNotShowingWithEmptyOptionList.includes(control.$type) &&
    (!options || options.length === 0)
  );
}

/**
 * Check select visibility by select hiding logic
 *
 * @param state - form state
 * @param control - control (subfield) metadata
 * @param field - field config
 * @param options - original select options
 */
export function isSelectShouldBeHidden(
  state: ISPFormState,
  control: ISelect,
  field: IField,
  options: ISelectOption[] | null,
): boolean {
  const isHidden = state.hiddenService.isHidden(control.$name, field.$name);
  const isDepend = Boolean(control.$depend);
  const isSelectDependAndOnlyNullIsLeft =
    isDepend && options?.length === 1 && options?.[0].$key === 'null';
  const isSelectDependAndHasNoOptions =
    isDepend && (!options || options.length === 0);

  return (
    isHidden ||
    isSelectDependAndOnlyNullIsLeft ||
    isSelectDependAndHasNoOptions ||
    isHideSelectWithEmptyOptionsList(control, options)
  );
}

/**
 * Stream of select visibility state
 *
 * @param state
 * @param control
 * @param field
 * @param options$
 */
export function isSelectShouldBeHidden$(
  state: ISPFormState,
  control: ISelect,
  field: IField,
  options$: Observable<ISelectOption[] | null>,
): Observable<boolean> {
  return combineLatest([
    options$,
    state.hiddenService.isHidden$(field.$name),
  ]).pipe(
    map(([options]) => isSelectShouldBeHidden(state, control, field, options)),
  );
}

/**
 * Autoselect first option from all select options
 *
 * @param options - select options
 * @param model - form model
 * @param fieldConfig - field config
 * @param allowNull - allow autoslect 'null' as first option. Extform license.order and handlers table filter case
 */
export function autoPickSelectOptions(
  options: ISelectOption[],
  model: IFormModel,
  fieldConfig: ISPFieldConfig<
    | ISPFieldType.Radio
    | ISPFieldType.RadioImg
    | ISPFieldType.Select
    | ISPFieldType.Switcher
    | ISPFieldType.SwitcherSelect
  >,
  allowNull?: boolean,
): void {
  if (
    fieldConfig.props.isHidden ||
    fieldConfig.props.disabled ||
    !fieldConfig.key ||
    !options.length
  ) {
    return;
  }

  const value = model[fieldConfig.key];

  let valueArray: string[];
  if (Array.isArray(value)) {
    valueArray = value;
  } else {
    // @WARN you should split options only for multiple select!
    // There can be simple select with options, that contain ',' in key
    if (
      fieldConfig.type === ISPFieldType.Select &&
      fieldConfig.props.multiple
    ) {
      valueArray = value ? String(value).split(',') : [];
    }
    valueArray = value ? [String(value)] : [];
  }

  const currentValueIsInOptions = valueArray.every(v =>
    options.some(o => o.$key === v),
  );

  if (currentValueIsInOptions) {
    return;
  }
  // ...mean we must change formControl value, if there is no such options

  const haveOptionsToChoose = options.length > 0;
  const isNotSelectOrSingleSelect =
    fieldConfig.type === ISPFieldType.Radio ||
    fieldConfig.type === ISPFieldType.RadioImg ||
    fieldConfig.type === ISPFieldType.Switcher ||
    fieldConfig.type === ISPFieldType.SwitcherSelect ||
    !fieldConfig.props.multiple;

  // single select automatically select first available option
  if (isNotSelectOrSingleSelect && haveOptionsToChoose) {
    const firstOptionValue = options[0].$key;
    const firstOptionIsNotNull = firstOptionValue !== 'null';
    const isOptionValueDistinct = firstOptionValue !== value;

    if (isOptionValueDistinct && (firstOptionIsNotNull || allowNull)) {
      fieldConfig.formControl?.setValue(firstOptionValue, {
        emitEvent: true,
      });
      return;
    }
  }

  const hasNullOption = options.some(option => option.$key === 'null');
  fieldConfig.formControl?.setValue(hasNullOption ? 'null' : '', {
    emitEvent: true,
  });
}

/**
 * Get string with selectors of select dropdown elements, on scrolling of which you need to close tooltip
 *
 * @param dropdownParentSelector - parent selector for select dropdown
 */
function getScrollSelectorForTooltip(dropdownParentSelector: string): string {
  let scrollSelector = 'ispui-dropdown-popup ispui-select-list';
  if (dropdownParentSelector) {
    scrollSelector += `, ${dropdownParentSelector}`;
  }

  return scrollSelector;
}

/**
 * Wrap text with div, that has attributes for tooltip
 *
 * @param text - text to wrap
 * @param dropdownParentSelector - parent selector for select dropdown
 */
export function wrapTextWithTooltip(
  text: string,
  dropdownParentSelector?: string,
): string {
  const commonTooltipAttributes =
    'data-ispui-tooltip data-ispui-tooltip-ellipsis data-ispui-tooltip-disabled-arrow';
  const scrollSelectorAttribute = `data-ispui-tooltip-scroll-selector="${
    dropdownParentSelector
      ? getScrollSelectorForTooltip(dropdownParentSelector)
      : ''
  }"`;
  const placementOrderAttribute =
    'data-ispui-tooltip-placement-order="[&quot;right&quot;,&quot;left&quot;,&quot;bottom&quot;,&quot;top&quot;]"';

  return `<div ${commonTooltipAttributes} ${scrollSelectorAttribute} ${placementOrderAttribute}>${text}</div>`;
}

/**
 * Wrap text with span, that has style font family
 *
 * @param text - text to wrap
 * @param font - font
 */
function wrapTextWithFont(text: string, font: string): string {
  return `<span style="font-family: ${font}">${text}</span>`;
}

/**
 * Add span that has image background-image, and wrap it with span
 *
 * @param text - text to wrap
 * @param imageURL - image url
 */
function wrapTextWithImage(text: string, imageURL: string): string {
  const imageTemplate = `<span class="select__option-image" style="background-image: url('${imageURL}');"></span>`;

  return `<span class="select__option-container">${imageTemplate}${text}</span>`;
}

/**
 * Add color to placeholder
 *
 * @param text - text to wrap
 */
function wrapTextPlaceholder(text: string): string {
  return `<span style="color: var(${ISP_COLORS_VAR.textAdditional})">${text}</span>`;
}

/**
 * Get the html template of an option for when the option has an image and when it doesn't, or has custom font
 *
 * @param option - select option data from doc.slist
 * @param optionText - option text, which may contain html as search result among options
 * @param dropdownParentSelector - parent selector for select dropdown
 * @param hasFont - is BE send font
 */
function getHTMLTemplateForOption(
  option: ISelectOption,
  optionText: string,
  dropdownParentSelector?: string,
  hasFont = false,
): string {
  let htmlTextTemplate = optionText;

  if (hasFont) {
    htmlTextTemplate = wrapTextWithFont(htmlTextTemplate, option.$key);
  }

  htmlTextTemplate = wrapTextWithTooltip(
    htmlTextTemplate,
    dropdownParentSelector,
  );

  if (!option.$image && htmlTextTemplate) {
    return htmlTextTemplate;
  }

  return wrapTextWithImage(htmlTextTemplate, option.$image!);
}

/**
 * Get the html template of button for when the option has an image and when it doesn't, or has custom font
 *
 * @param option - select option data from doc.slist
 * @param optionText - option text, which may contain html as search result among options
 * @param placeholder - placeholder
 * @param hasFont - is BE send font
 */
function getHTMLTemplateForButton(
  option: ISelectOption | ISelectOption[] | undefined,
  optionText: string | null,
  placeholder: string,
  hasFont: boolean,
): string {
  if (!option) {
    return wrapTextPlaceholder(placeholder);
  }

  let htmlTextTemplate = optionText || '';

  if (Array.isArray(option)) {
    if (option.length === 0) {
      return wrapTextPlaceholder(placeholder);
    }
    htmlTextTemplate = option
      .map(o => (hasFont ? wrapTextWithFont(o.$, o.$key) : o.$))
      .join(', ');

    return htmlTextTemplate;
  }

  if (hasFont) {
    htmlTextTemplate = wrapTextWithFont(
      htmlTextTemplate,
      (option as ISelectOption).$key,
    );
  }

  if (!option.$image) {
    return htmlTextTemplate;
  }

  return wrapTextWithImage(htmlTextTemplate, option.$image);
}

/**
 * Get selected options by select value
 *
 * @param value
 * @param options
 */
function getSelectedOption(
  value: string[] | string,
  options: ISelectOption[],
): ISelectOption | ISelectOption[] | undefined {
  return Array.isArray(value)
    ? options.filter(selectOption => value.includes(selectOption.$key))
    : options.find(selectOption => selectOption.$key === value);
}

/**
 * Get function for select button text calculating
 *
 * @param options
 * @param control
 * @param sanitizer
 */
export function getButtonTextFunc(
  options: SelectOptionsState,
  control: ISelect,
  sanitizer: DomSanitizer,
): SelectBuilderParams['buttonTextFuncHTML'] {
  return (state, placeholder, buttonText) => {
    const selectedOption = getSelectedOption(state.value, options.get() || []);

    return getHTMLTemplateForButton(
      selectedOption,
      sanitizer.sanitize(SecurityContext.HTML, buttonText),
      placeholder,
      Boolean(control.$targetfontfamilyview),
    );
  };
}

/**
 * Get function for select button text calculating. For chips mode
 *
 * @param options
 * @param state
 * @param control
 * @param field
 */
function getButtonTextForChip(
  options: SelectOptionsState,
  state: ISPFormState,
  control: ISelect,
  field: IField,
): SelectBuilderParams['buttonTextFunc'] {
  return selectState => {
    const doc = state.docSubject.value;
    const label =
      control.$custom_label === 'yes'
        ? DocHelper.getMessage(control.$name, doc)
        : getLabel(field, doc);

    const placeholder = DocHelper.getMessage(`${control.$name}_nullmsg`, doc);

    const selectedOption = getSelectedOption(
      selectState.value,
      options.get() || [],
    );

    const isNoOption = !selectedOption;
    const isEmptyArray =
      Array.isArray(selectedOption) && selectedOption.length === 0;
    if (isNoOption || isEmptyArray) {
      return placeholder ? `${label}: ${placeholder}` : label;
    }

    if (!Array.isArray(selectedOption)) {
      return `${label}: ${selectedOption.$}`;
    }

    if (selectedOption.length === 1) {
      return `${label}: ${selectedOption[0].$}`;
    }

    return `${label}: (${selectedOption.length})`;
  };
}

/**
 * Map select options to radio img options
 *
 * @param options
 * @param control
 * @param sanitizer
 * @param dropdownParentSelector - parent selector for select dropdown
 */
function getSelectOptions(
  options: ISelectOption[],
  control: ISelect,
  sanitizer: DomSanitizer,
  dropdownParentSelector?: string,
): Option[] {
  return options.map(option => {
    const htmlFunc: Option['html'] = optionUI =>
      getHTMLTemplateForOption(
        option,
        sanitizer.sanitize(SecurityContext.HTML, optionUI.textHtml) || '',
        dropdownParentSelector,
        Boolean(control.$targetfontfamilyview),
      );

    return {
      text: option.$,
      value: option.$key,
      color: option.$color
        ? {
            yellow: ISP_COLORS.backgroundWarning,
            blue: ISP_COLORS.textBlue,
            red: ISP_COLORS.textDanger,
            green: ISP_COLORS.textSuccess,
            cyan: ISP_COLORS.textCyan,
          }[option.$color]
        : undefined,
      html: htmlFunc,
    };
  });
}

/**
 * Get props for select field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 * @param page
 * @param custom - custom options for select
 */
export function getSelectProps(
  control: ISelect,
  field: IField,
  state: ISPFormState,
  page?: IFormPage,
  custom?: SelectOptionsState,
): SelectProps {
  const selectOptions: SelectOptionsState = custom || {
    get: () =>
      state.selectService.getOptionsForSelect(control.$name, control.$depend),
    stream$: state.selectService.getOptionsForSelect$(
      control.$name,
      control.$depend,
    ),
  };

  const options = selectOptions.get() || [];
  const options$ = selectOptions.stream$.pipe(map(o => o || []));

  const type =
    control.$type === SelectType.Chip ||
    control.$type === SelectType.ChipMultiple
      ? 'chip'
      : undefined;

  const multiple =
    control.$type === SelectType.Multiple ||
    control.$type === SelectType.ChipMultiple;
  const dropdownParentSelector = getDropdownParentSelector(
    state,
    control,
    field,
    page,
  );
  const props: SelectProps = {
    originalControl: control,
    originalField: field,
    originalPage: page,
    fieldSettings$: of({
      displayMode: 'input-like',
      tagPlace: 'field',
    } as FieldSettings),
    anchorType: type,
    options$,
    optionsUi$: options$.pipe(
      map(originalOptions =>
        getSelectOptions(
          originalOptions,
          control,
          state.sanitizer,
          dropdownParentSelector,
        ),
      ),
    ),
    multiple,
    placeholder$: state.mixedService.getControlPlaceholder$(
      control,
      ISPFieldType.Select,
    ),
    notFoundText$: DocHelper.getMessage$(
      'select_option_not_found',
      state.docSubject,
    ),
    listWidth$: matchMediaBreakpointDown$('sm').pipe(
      map(isLessSm => (isLessSm ? '100%' : state.selectDropdownWidth)),
    ),
    selectAllText$: DocHelper.getMessage$('msg_select_all', state.docSubject),
    anchorWidth: state.selectAnchorWidth,
    dropdownScrollSelector: state.dropdownScrollSelector,
    dropdownParentSelector: dropdownParentSelector,
    dropdownDirection: getDropdownDirection(control, field, page),
    anchorTextFuncHTML:
      type !== 'chip'
        ? getButtonTextFunc(selectOptions, control, state.sanitizer)
        : undefined,
    anchorTextFunc:
      type === 'chip'
        ? getButtonTextForChip(selectOptions, state, control, field)
        : undefined,
    isHidden: isSelectShouldBeHidden(state, control, field, options),
    isHidden$: isSelectShouldBeHidden$(state, control, field, options$),
  };

  return props;
}

/**
 * Get config for select field
 *
 * @param control - control (subfield) metadata
 * @param state - dynamic form state
 * @param custom - custom options for select
 */
export function getSelectConfig(
  control: SelectControlWithValue,
  state: ISPFormState,
  custom?: SelectOptionsState,
): ISPFieldConfig<ISPFieldType.Select> {
  const props = getSelectProps(
    control.control,
    control.parent.field,
    state,
    control.parent.parent?.page,
    custom,
  );

  return {
    key: control.control.$name,
    type: ISPFieldType.Select,
    wrappers: [ISPFieldWrapper.FieldBase, ISPFieldWrapper.ValidationError],
    props,
    modelOptions: {
      updateOn: 'change',
    },
  };
}
